/**
 * DrManiProgressTimer - animated bar for tap-to-continue interstitial screens.
 * DMM-native counterpart to CitrusQuiz/ProgressTimer (uses dmm-progress-timer-* classes).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface DrManiProgressTimerProps {
  duration?: number;
  onComplete?: () => void;
  resetKey?: string | number;
}

const DrManiProgressTimer: React.FC<DrManiProgressTimerProps> = ({
  duration = 5000,
  onComplete,
  resetKey,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const hasCompleted = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const complete = useCallback(() => {
    if (hasCompleted.current) return;
    hasCompleted.current = true;
    setIsRunning(false);
    onCompleteRef.current?.();
  }, []);

  useEffect(() => {
    hasCompleted.current = false;
    setIsRunning(false);

    if (duration <= 0) {
      complete();
      return undefined;
    }

    let completionTimer = 0;
    const startFrame = window.requestAnimationFrame(() => {
      setIsRunning(true);
      completionTimer = window.setTimeout(complete, duration);
    });

    return () => {
      window.cancelAnimationFrame(startFrame);
      window.clearTimeout(completionTimer);
    };
  }, [complete, duration, resetKey]);

  return (
    <div className="dmm-progress-timer">
      <div className="dmm-progress-timer-track">
        <div
          className={`dmm-progress-timer-fill ${isRunning ? 'dmm-progress-timer-fill-running' : ''}`}
          style={{ transitionDuration: isRunning ? `${duration}ms` : '0ms' }}
        />
      </div>
    </div>
  );
};

export default DrManiProgressTimer;
